import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { Spin, Alert } from "antd";
import { useTranslation } from "react-i18next";
// Icons
import {
  PictureFilled,
  CalculatorFilled,
  WechatFilled,
  ReadFilled,
  EnvironmentFilled,
  AppstoreFilled,
} from "@ant-design/icons";
// GraphQL
import GET_SURVEY_QUESTION from "graphql/queries/GetSurveyQuestion";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
// Styles
import {
  Container,
  Question,
  UpperCaseHeading,
  AnswersCard,
  CardContent,
  IconContainer,
  StyledCheckbox,
  ContinueButton,
  ResponsiveGrid,
} from "./MultipleChoicesQuestionStyle";

type IconMapping = {
  [key: number]: JSX.Element;
};

const iconMapping: IconMapping = {
  8: <ReadFilled style={{ fontSize: "24px" }} />,
  9: <CalculatorFilled style={{ fontSize: "24px" }} />,
  10: <PictureFilled style={{ fontSize: "24px" }} />,
  11: <WechatFilled style={{ fontSize: "24px" }} />,
  12: <EnvironmentFilled style={{ fontSize: "24px" }} />,
  13: <AppstoreFilled style={{ fontSize: "24px" }} />,
};

type LanguageId =
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "39"
  | "40"
  | "41"
  | "42"
  | "43"
  | "44";

const languageNames: Record<LanguageId, string> = {
  1: "Mandarin (Simplified) ",
  2: "Tiếng Việt",
  3: "Shanghainese",
  4: "Japanese",
  5: "Korean",
  6: "Cantonese",
  39: "Taiwanese ",
  40: "Mandarin (Traditional) ",
  41: "Russian",
  42: "Italian",
  43: "Spanish",
  44: "French",
};
interface ReasonForLearningProps {
  onChange: (data: { reasons: number[] }) => void;
  createSurveyResponse: (surveyQuestionId: number, answerId: number) => void;
  language: string;
}

const ReasonForLearning = ({
  onChange,
  createSurveyResponse,
  language,
}: ReasonForLearningProps) => {
  const [selectedReasons, setSelectedReasons] = useState<number[]>([]);
  const { t } = useTranslation("registration_survey");
  const languageName = languageNames[language as LanguageId] || language;

  const { error, loading, data } = useQuery(GET_SURVEY_QUESTION, {
    variables: {
      order: 1,
      surveyType: 0,
    },
  });

  if (loading) return <Spin />;
  if (error) return <Alert message="Error loading question" type="error" />;

  const surveyQuestion = data?.surveyQuestion;

  if (!surveyQuestion)
    return <Alert message="Question not found" type="warning" />;

  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    const { value, checked } = e.target;
    setSelectedReasons((prev) =>
      checked ? [...prev, value] : prev.filter((v) => v !== value)
    );
  };

  const handleSubmit = async () => {
    if (selectedReasons.length > 0) {
      onChange({ reasons: selectedReasons });
      await Promise.all(
        selectedReasons.map(async (reasonId) => {
          createSurveyResponse(surveyQuestion.id, reasonId);
        })
      );
    }
  };

  return (
    <Container>
      <Question>
        <UpperCaseHeading>
          {t(surveyQuestion.questionText.trim(), { language: languageName })}
        </UpperCaseHeading>
      </Question>
      <AnswersCard bodyStyle={{ padding: 0 }}>
        {surveyQuestion.surveyAnswers.map((answer: any) => (
          <ResponsiveGrid key={answer.id}>
            <CardContent
              key={answer.id}
              isSelected={selectedReasons.includes(answer.id)}
              onClick={() =>
                handleCheckboxChange({
                  target: {
                    value: answer.id,
                    checked: !selectedReasons.includes(answer.id),
                  },
                } as CheckboxChangeEvent)
              }
            >
              <div className="icon-text-group">
                <IconContainer>{iconMapping[answer.id]}</IconContainer>
                <span>
                  {t(answer.answerText.trim(), { language: languageName })}
                </span>
              </div>
              <StyledCheckbox
                checked={selectedReasons.includes(answer.id)}
                onChange={handleCheckboxChange}
                value={answer.id}
              />
            </CardContent>
          </ResponsiveGrid>
        ))}
        <ContinueButton
          type="primary"
          onClick={handleSubmit}
          disabled={selectedReasons.length === 0}
        >
          CONTINUE
        </ContinueButton>
      </AnswersCard>
    </Container>
  );
};

export default ReasonForLearning;
